<template>
  <Page :class="{ toggle: toggleSidebar }">
    <ManageCollectionItemModal
      v-if="isManageCollectionItemModalShowing"
      :collectionType="loadItem.collectionType"
      :itemId="loadItem.itemId"
      :onClose="onCloseManageCollectionItemModal" />
    <FilterSidebar
      class="sidebar"
      v-if="toggleSidebar"
      :onClose="handleToggleSidebar" />
    <template v-slot:page-primary v-if="pinnedCollections && pinnedCollections.length > 0">
      <div class="container">
        <CollectionSuggestion
          :collections="pinnedCollections"
          :collectionType="'video'">
          <template v-slot:collection-header>
            <i class="fas fa-bookmark mr-1" /> เพลย์ลิสต์ปักหมุด
          </template>
        </CollectionSuggestion>
      </div>
    </template>
    <template v-slot:page-header>
      <div class="container">
        <div class="page-header">
          <div class="title-container">
            <div class="title">วิดีโอ</div>
          </div>
          <div class="actions">
            <router-link :to="'/videos/import'" class="btn btn-outline-secondary mr-3">
              <i class="fa fa-cloud-download icon-align-left"></i> นำเข้าวิดีโอ
            </router-link>
            <router-link :to="'/videos/upload'" class="btn btn-primary">
              <i class="fas fa-cloud-upload-alt icon"></i> อัปโหลด
            </router-link>
          </div>
        </div>
      </div>
    </template>
    <div class="container">
      <PreloaderSection v-if="loadVideosListState.status ==='loading'" />
      <div>
        <!-- render content -->
        <Section v-if="loadVideosListState.status ==='success'">
          <template v-slot:section-filter>
            <div class="search-container">
              <input
                class="form-control form-control-search"
                :value="filter.q"
                placeholder="ค้นหาด้วยชื่อวิดีโอ"
                @change="onSearch" />
              <div
                class="filter-button ml-2"
                @click="handleToggleSidebar">
                <i class="fa fa-filter icon mr-2" />
                <span class="filter-label">ค้นหาขั้นสูง</span>
              </div>
            </div>
          </template>
          <!-- render content -->
          <template v-slot:section-body>
            <div v-if="!isEmpty()">
              <List>
                <template v-slot:list-header>
                  <div class="list-item-image"></div>
                  <div class="list-item-detail">
                    <div class="list-item list-item-primary title-information">
                      ชื่อ
                    </div>
                    <div class="list-item">หมวดหมู่</div>
                    <div class="list-item list-item-create-at">ออกอากาศครั้งแรก</div>
                    <div class="list-item list-item-status">สถานะ</div>
                    <div class="list-item list-item-action"></div>
                  </div>
                </template>
                <template v-slot:list-body>
                  <div
                    class="list-item-container no-underline"
                    v-for="(item, itemIndex) in videos"
                    :key="item.id"
                    @click.stop="goToPageDetail(getRedirectLink(item), itemIndex, $event)">
                    <div class="list-item-image">
                      <ImageView
                        :source="item.coverImage"
                        imageSize="cover160w90h" />
                    </div>
                    <div class="list-item-detail">
                      <div class="list-item list-item-primary title-information">
                        <div class="information-wrapper">
                          <div class="subtitle">
                            {{ getProgramName(item.program) }}
                          </div>
                          <div class="title">{{ item.title }}</div>
                        </div>
                        <span class="badge badge-live" v-if="item.type === 'live'">LIVE</span>
                      </div>
                      <div class="list-item">
                        {{ getCategoryName(item.category) }}
                      </div>
                      <div class="list-item list-item-create-at">
                        {{ item.firstAiredAt ? formatDateTime(item.firstAiredAt) : '-' }}
                      </div>
                      <div class="list-item list-item-status">
                        <div class="badge badge-status" :class="item.status">
                          {{ item.status }}
                        </div>
                      </div>
                      <div class="list-item list-item-action">
                        <div class="action-container">
                          <button
                            type="button"
                            class="btn btn-light btn-action"
                            :title="'เพิ่มวิดีโอนี้เข้าเพลย์ลิสต์'"
                            @click.stop="onToggleManageCollectionItemModal(item.id)">
                            <i class="far fa-plus-square icon" />
                          </button>
                          <router-link
                            class="btn btn-light btn-action"
                            :to="getRedirectLink(item)">
                            <i class="fas fa-pencil-alt"></i>
                          </router-link>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </List>
              <div class="pagination-container" v-if="isShowPagination()">
                <Pagination :lastPage="lastPage" />
              </div>
            </div>
            <!-- empty list --->
            <EmptyMessage v-if="isEmpty()">
              <h3>ไม่มีข้อมูล</h3>
              <router-link
                class="btn btn-light btn-lg mt-4"
                :to="`/videos/create`">
                อัปโหลดวิดีโอ
              </router-link>
            </EmptyMessage>
            <!-- search result -->
            <EmptyMessage v-if="isEmptySearch()">
              <h3>ไม่พบวิดีโอที่ค้นหา</h3>
              <button
                type="button"
                class="btn btn-light btn-lg mt-4"
                @click="clearSearch">
                ล้างการค้นหา
              </button>
            </EmptyMessage>
          </template>
        </Section>
        <!-- error from api -->
        <ErrorMessageVideoContainer
          v-if="loadVideosListState.status === 'error'"
          :error="loadVideosListState.error" />
      </div>
    </div>
  </Page>
</template>

<script>
import _ from 'lodash';
import { mapState } from 'vuex';
import { loadCollections } from '@/collection/api';
import dateFormetter from '@/utils/dateFormatter';

import Page from '@/layouts/Page.vue';
import Pagination from '@/components/Pagination.vue';
import Section from '@/components/Section.vue';
import List from '@/components/List.vue';
import EmptyMessage from '@/components/EmptyMessage.vue';
import PreloaderSection from '@/components/PreloaderSection.vue';
import FilterSidebar from '@/components/FilterSidebar.vue';
import ImageView from '@/image/ImageView.vue';
import ManageCollectionItemModal from '@/collection/ManageCollectionItemModal.vue';
import CollectionSuggestion from '@/collection/CollectionSuggestion.vue';
import ErrorMessageVideoContainer from '@/video/ErrorMessageVideoContainer.vue';

export default {
  mixins: [dateFormetter],
  components: {
    Page,
    Pagination,
    ImageView,
    Section,
    List,
    EmptyMessage,
    ErrorMessageVideoContainer,
    PreloaderSection,
    CollectionSuggestion,
    FilterSidebar,
    ManageCollectionItemModal,
  },
  data() {
    return {
      pinnedCollections: null,
      selectedStatus: 'all',
      toggleSidebar: false,
      isManageCollectionItemModalShowing: false,
      loadItem: {
        itemId: '',
        collectionType: 'video',
      },
    };
  },
  computed: {
    ...mapState({
      videos: (state) => state.videosList.videos,
      loadVideosListState: (state) => state.videosList.loadVideosListState,
      lastPage: (state) => state.videosList.lastPage,
      contentFilter: (state) => state.filter.filter,
    }),
    filter() {
      return this.$route.query;
    },
    isMultipleUpload() {
      return this.$route.query.multiple;
    },
  },
  watch: {
    filter(newValue) {
      this.loadVideos(newValue);
    },
    isMultipleUpload(multiple) {
      if (multiple) {
        this.loadVideos(this.filter);
      }
    },
  },
  created() {
    this.loadVideos(this.filter);
    this.loadCollections();
  },
  methods: {
    loadVideos(filter) {
      const params = { ...filter, type: 'vod' };
      this.$store.dispatch('loadVideosList', params);
    },
    clearSearch() {
      this.$store.dispatch('clearFilter');
      this.$router.push({ query: null });
    },
    async loadCollections() {
      const filter = {
        isPinned: true,
        type: 'video',
      };
      const response = await loadCollections(filter);
      this.pinnedCollections = response.data.data;
    },
    handleToggleSidebar() {
      this.toggleSidebar = !this.toggleSidebar;
    },
    onSearch(event) {
      this.$store.dispatch('setFilterState', { q: event.target.value });
      if (this.contentFilter.q) {
        this.$router.push({ name: 'videoList', query: { q: this.contentFilter.q, ...this.contentFilter } });
      } else {
        this.$router.push({ name: 'videoList', query: { ...this.contentFilter } });
      }
    },
    isEmptySearch() {
      return _.isEmpty(this.videos) && !_.every(this.filter, _.isEmpty);
    },
    isEmpty() {
      return _.isEmpty(this.videos) && _.every(this.filter, _.isEmpty);
    },
    isShowPagination() {
      return this.lastPage > 1;
    },
    getCategoryName(category) {
      return _.get(category, 'name', 'ไม่มีหมวดหมู่');
    },
    onToggleManageCollectionItemModal(itemId) {
      this.loadItem.itemId = itemId;
      this.isManageCollectionItemModalShowing = !this
        .isManageCollectionItemModalShowing;
    },
    onCloseManageCollectionItemModal() {
      this.isManageCollectionItemModalShowing = false;
    },
    getProgramName(program) {
      return _.get(program, 'title', null);
    },
    getRedirectLink(video) {
      const videoType = _.get(video, 'type', 'vod');
      if (videoType === 'live') {
        return `/live/${video.id}`;
      }
      return `/videos/${video.id}`;
    },
  },
  beforeDestroy() {
    this.$store.dispatch('clearFilter');
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/layouts/page.scss';
@import './src/assets/scss/themes/components/form.scss';

.toggle {
  margin-right: 250px;
  transition: all 0.3s ease-out;
}

.title-information {
  display: flex;
  justify-content: space-between;
  .badge-live {
    padding-top: $spacer * 0.25;
    padding-bottom: $spacer * 0.25;
    font-size: $font-size-base * 0.5;
    width: 26px;
    margin: 0 $spacer * 0.5;
    background-color: lighten($red, 5%);
    color: white;
  }
}

@media (max-width: 575.98px) {
  .toggle {
    margin-right: 0px;
  }
  .playlist-section {
    margin-top: $spacer * 1.5;
    margin-bottom: 0;
    .title-container {
      margin-bottom: $spacer;
      display: flex;
    }
    .playlist-container {
      display: flex;
      .playlist {
        flex: 0 0 85%;
        margin-right: $spacer;
        position: relative;
      }
    }
  }
}

@media (min-width: 576px) and (max-width: 1023.98px) {
  .toggle {
    margin-right: 0px;
  }

  .list-item-container {

    .list-item-detail {
      .list-item {
        &.list-item-status {
          display: none;
        }
        &.list-item-create-at {
          display: none;
        }
        &.list-item-action {
          .action-container {
            .btn-external {
              display: none;
            }
          }
        }
      }
    }
  }
}
</style>
